import * as React from "react"

import { applyInputMask, getInputMaskMode, type InputMask } from "@/lib/input-mask"
import { cn } from "@/lib/utils"

interface InputProps extends React.ComponentPropsWithoutRef<"input"> {
  mask?: InputMask
}

const NUMBER_NAVIGATION_KEYS = new Set([
  "Backspace",
  "Delete",
  "Tab",
  "Escape",
  "Enter",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
])

function canUseNegativeValue(min?: string | number): boolean {
  if (min === undefined || min === null || min === "") {
    return false
  }

  return Number(min) < 0
}

function sanitizeNumberInput(value: string, allowNegative: boolean): string {
  if (!value) {
    return ""
  }

  const normalized = value.replace(",", ".").replace(/[^\d.-]/g, "")
  const isNegative = allowNegative && normalized.startsWith("-")
  const unsignedValue = normalized.replace(/-/g, "")
  const [integerPart = "", ...decimalParts] = unsignedValue.split(".")

  if (!decimalParts.length) {
    return `${isNegative ? "-" : ""}${integerPart}`
  }

  return `${isNegative ? "-" : ""}${integerPart}.${decimalParts.join("")}`
}

const Input = React.forwardRef<
  HTMLInputElement,
  InputProps
>(({ className, type, mask, inputMode, onChange, onKeyDown, onPaste, value, defaultValue, min, ...props }, ref) => {
  const isNumberInput = type === "number"
  const allowNegative = isNumberInput && canUseNegativeValue(min)
  const maskedValue =
    mask && typeof value === "string"
      ? applyInputMask(mask, value)
      : value

  const maskedDefaultValue =
    mask && typeof defaultValue === "string"
      ? applyInputMask(mask, defaultValue)
      : defaultValue

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isNumberInput) {
        const nextValue = sanitizeNumberInput(event.currentTarget.value, allowNegative)
        event.currentTarget.value = nextValue
      }

      if (mask) {
        const nextValue = applyInputMask(mask, event.currentTarget.value)
        event.currentTarget.value = nextValue
      }

      onChange?.(event)
    },
    [allowNegative, isNumberInput, mask, onChange]
  )

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event)

      if (event.defaultPrevented || !isNumberInput || event.ctrlKey || event.metaKey || event.altKey) {
        return
      }

      if (NUMBER_NAVIGATION_KEYS.has(event.key)) {
        return
      }

      if (event.key === "-" && allowNegative && event.currentTarget.selectionStart === 0 && !event.currentTarget.value.includes("-")) {
        return
      }

      if (event.key.length === 1 && !/[\d.,]/.test(event.key)) {
        event.preventDefault()
      }
    },
    [allowNegative, isNumberInput, onKeyDown]
  )

  const handlePaste = React.useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      onPaste?.(event)

      if (event.defaultPrevented || !isNumberInput) {
        return
      }

      const pastedValue = event.clipboardData.getData("text")
      if (sanitizeNumberInput(pastedValue, allowNegative) !== pastedValue.replace(",", ".")) {
        event.preventDefault()
      }
    },
    [allowNegative, isNumberInput, onPaste]
  )

  return (
    <input
      type={type}
      inputMode={inputMode ?? (mask ? getInputMaskMode(mask) : isNumberInput ? "decimal" : undefined)}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus:placeholder:text-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      min={min}
      value={maskedValue}
      defaultValue={maskedDefaultValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
